import React, { useRef } from 'react';
/**
 * Return a getter to the element that we can listen to scroll on
 * @param containerProps The props for the resopnsive container
 * @param props The entire props of the component
 * @param customOverflowWrapperGetter An optional function to return an element without
 * the default way
 * @returns Getter to the element that we should listen for scroll
 */
const memoizedOverflowWrapperProvider = (containerProps, props, customOverflowWrapperGetter) => {
    let overFlowWrapper;
    return {
        getCachedWrapper: () => {
            if (!overFlowWrapper) {
                overFlowWrapper = customOverflowWrapperGetter
                    ? customOverflowWrapperGetter(props)
                    : document.querySelector(`.${containerProps.overlowWrapperClassName}`);
            }
            return overFlowWrapper;
        },
        cleanCache: () => (overFlowWrapper = undefined),
    };
};
const isResponsiveContainerProps = (props) => !!props.responsiveContainerProps;
const isClassicContainerProps = (props) => !!props.containerProps;
const getContainerProps = (props) => {
    let containerProps;
    if (isClassicContainerProps(props)) {
        containerProps = props.containerProps;
    }
    else if (isResponsiveContainerProps(props)) {
        containerProps = props.responsiveContainerProps;
    }
    return containerProps;
};
const getScrollApi = (overflowWrapperGetter) => ({
    scrollApi: {
        getScrollTop: () => overflowWrapperGetter()?.scrollTop || 0,
        getScrollLeft: () => overflowWrapperGetter()?.scrollLeft || 0,
        scroll: (x, y) => {
            overflowWrapperGetter()?.scrollTo(x, y);
        },
        scrollBy: (x, y) => {
            overflowWrapperGetter()?.scrollBy(x, y);
        },
        onScroll: (cb) => {
            overflowWrapperGetter()?.addEventListener('scroll', cb);
            return () => {
                overflowWrapperGetter()?.removeEventListener('scroll', cb);
            };
        },
    },
});
export function withScrollAPIForResponsiveContainer(ViewerComponent, scrollAPIOptions = {
    alwaysHasOverflow: false,
}) {
    return React.forwardRef((props, ref) => {
        const containerProps = getContainerProps(props);
        const overflowWrapperGetter = scrollAPIOptions.overflowWrapperGetter;
        const compRef = useRef();
        const overFlowWrapperRef = React.useMemo(() => memoizedOverflowWrapperProvider(containerProps, props, overflowWrapperGetter), [props, containerProps, overflowWrapperGetter]);
        React.useImperativeHandle(ref, () => {
            if ((scrollAPIOptions.alwaysHasOverflow &&
                scrollAPIOptions.overflowWrapperGetter) ||
                (containerProps &&
                    containerProps.hasOverflow &&
                    containerProps.hasScrollOverflow)) {
                return {
                    ...compRef.current,
                    ...getScrollApi(overFlowWrapperRef.getCachedWrapper),
                };
            }
            return { ...compRef.current };
        }, [overFlowWrapperRef, containerProps]);
        React.useEffect(() => {
            overFlowWrapperRef.cleanCache();
        }, [overFlowWrapperRef]);
        return React.createElement(ViewerComponent, { ...props, ref: compRef });
    });
}
//# sourceMappingURL=scrollablePreviewWrapper.js.map